// src/pages/PanelFirma/FirmaCartasPoder.js
// Migrado desde ReadjudicacionMasiva - firma de cartas poder de readjudicacion

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Typography,
    Box,
    Paper,
    IconButton,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Checkbox,
    LinearProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    TableSortLabel,
    TablePagination
} from '@mui/material';
import {
    Description as DescriptionIcon,
    CheckCircle as CheckCircleIcon,
    GetApp as DownloadIcon,
    CloudUpload as UploadIcon,
    Visibility as ViewIcon,
    Warning as WarningIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    VpnKey as TokenIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import jsPDF from 'jspdf';
import { ESCUDO_BASE64, ESCUDO_WIDTH_MM, ESCUDO_HEIGHT_MM } from '../../utils/escudoFiscalia';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import useReadjudicacion from '../../hooks/useReadjudicacion';
import { fetchFirmaLocal } from '../../hooks/useServicioFirma';
import DocumentoViewer from '../../components/ReadjudicacionMasiva/Documentos/DocumentoViewer';

const styles = {
    paper: {
        padding: '16px',
        marginBottom: '16px',
        borderRadius: '4px',
    },
    filterRow: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    chipFiltro: {
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { opacity: 0.85, transform: 'scale(1.05)' },
    },
    chipActivo: {
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.5)',
    },
    tableContainer: {
        maxHeight: 550,
    },
    estadoChip: {
        fontWeight: 600
    },
    sinPdf: {
        backgroundColor: '#bdbdbd',
        color: '#fff'
    },
    pendiente: {
        backgroundColor: '#ed6c02',
        color: '#fff'
    },
    firmado: {
        backgroundColor: '#2e7d32',
        color: '#fff'
    },
    actionButton: {
        marginRight: '4px'
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200,
    },
    uploadInput: {
        display: 'none'
    },
    bulkBar: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        marginBottom: '8px',
        padding: '8px 16px',
        backgroundColor: '#42a5f515',
        borderRadius: '4px',
        border: '1px solid #42a5f5',
        flexWrap: 'wrap',
    },
};

const FirmaCartasPoder = ({ onRefreshStats }) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);
    const bulkUploadRef = useRef(null);

    const {
        loading,
        listarCartasPendientesFirma,
        obtenerCartaPoderDetalle,
        subirPdfCartaPoder,
        descargarPdfBlob,
        subirPdfFirmado
    } = useReadjudicacion();

    const [cartas, setCartas] = useState([]);
    const [resumen, setResumen] = useState({ total: 0, pendientes: 0, firmadas: 0, sinPdf: 0 });
    const [filtroBusqueda, setFiltroBusqueda] = useState('');
    const [filtroEstado, setFiltroEstado] = useState('pendiente'); // default pendientes
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [documentoViewer, setDocumentoViewer] = useState({ open: false, titulo: '', contenido: '', carta: null });
    const [uploadDialog, setUploadDialog] = useState({ open: false, carta: null });
    const [generandoPdf, setGenerandoPdf] = useState(null);
    const [selected, setSelected] = useState([]);
    const [progresoDialog, setProgresoDialog] = useState({
        open: false, total: 0, actual: 0, generados: 0, errores: 0, currentName: '', tipo: ''
    });
    // Estado para firma digital desde el sistema
    const [firmaDialog, setFirmaDialog] = useState({ open: false, pin: '', firmando: false });
    const [servicioFirma, setServicioFirma] = useState({ disponible: null, tokens: 0 }); // null = no verificado
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuCarta, setMenuCarta] = useState(null);
    const [orderBy, setOrderBy] = useState('IdLote');
    const [orderDir, setOrderDir] = useState('asc');

    // URL del servicio local de firma
    const FIRMA_SERVICE_URL = process.env.REACT_APP_FIRMA_SERVICE_URL || 'http://127.0.0.1:5850';

    useEffect(() => {
        cargarCartas();
        verificarServicioFirma();
        const interval = setInterval(verificarServicioFirma, 5000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Verificar si el servicio local de firma esta disponible
    const verificarServicioFirma = async () => {
        try {
            const resp = await fetch(`${FIRMA_SERVICE_URL}/status`, { signal: AbortSignal.timeout(2000) });
            if (resp.ok) {
                const data = await resp.json().catch(() => ({}));
                setServicioFirma({ disponible: true, tokens: data.tokensDetectados ?? 0 });
            } else {
                setServicioFirma({ disponible: false, tokens: 0 });
            }
        } catch (err) {
            if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
                try {
                    await fetch(`${FIRMA_SERVICE_URL}/status`, { mode: 'no-cors', signal: AbortSignal.timeout(2000) });
                    // Servicio disponible pero sin CORS - no podemos saber tokens
                    setServicioFirma({ disponible: true, tokens: 0 });
                } catch {
                    setServicioFirma({ disponible: false, tokens: 0 });
                }
            } else {
                setServicioFirma({ disponible: false, tokens: 0 });
            }
        }
    };

    const cargarCartas = useCallback(async () => {
        try {
            const response = await listarCartasPendientesFirma(null, false);
            if (response) {
                const allCartas = response.cartas || [];
                setCartas(allCartas);
                setResumen({
                    total: allCartas.length,
                    sinPdf: allCartas.filter(c => c.EstadoFirma === 0).length,
                    pendientes: allCartas.filter(c => c.EstadoFirma === 1).length,
                    firmadas: allCartas.filter(c => c.EstadoFirma === 2).length
                });
                setSelected([]);
            } else {
                enqueueSnackbar('No se pudieron cargar las cartas poder', { variant: 'warning' });
            }
        } catch (error) {
            console.error('Error al cargar cartas poder:', error);
            enqueueSnackbar('Error al cargar las cartas poder', { variant: 'error' });
        }
        onRefreshStats?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- Filtrado client-side: texto libre + estado ---
    const cartasFiltradoBusqueda = filtroBusqueda
        ? cartas.filter(c => {
            const texto = filtroBusqueda.toLowerCase();
            return (
                (c.IdLote || '').toString().toLowerCase().includes(texto) ||
                (c.NumeroCartaPoder || '').toLowerCase().includes(texto) ||
                (c.Caratula || '').toLowerCase().includes(texto) ||
                (c.Autos || '').toLowerCase().includes(texto) ||
                (c.ExpedienteAdministrativo || '').toLowerCase().includes(texto)
            );
        })
        : cartas;

    const cartasFiltradasSinOrden = filtroEstado === 'pendiente'
        ? cartasFiltradoBusqueda.filter(c => c.EstadoFirma === 0 || c.EstadoFirma === 1)
        : filtroEstado !== null
            ? cartasFiltradoBusqueda.filter(c => c.EstadoFirma === filtroEstado)
            : cartasFiltradoBusqueda;

    // --- Ordenamiento ---
    const handleSort = (columna) => {
        if (orderBy === columna) {
            setOrderDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(columna);
            setOrderDir('asc');
        }
    };

    const cartasFiltradas = [...cartasFiltradasSinOrden].sort((a, b) => {
        const valA = a[orderBy] ?? '';
        const valB = b[orderBy] ?? '';
        let cmp;
        if (typeof valA === 'number' && typeof valB === 'number') {
            cmp = valA - valB;
        } else {
            cmp = String(valA).localeCompare(String(valB), 'es', { sensitivity: 'base' });
        }
        return orderDir === 'asc' ? cmp : -cmp;
    });

    // --- Chips como filtro ---
    const handleChipFiltro = (estado) => {
        setFiltroEstado(prev => prev === estado ? null : estado);
        setSelected([]);
    };

    // --- Seleccion multiple ---
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelected(cartasFiltradas.map(c => c.Id));
        } else {
            setSelected([]);
        }
    };

    const handleSelectOne = (id) => {
        setSelected(prev =>
            prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
        );
    };

    const isSelected = (id) => selected.includes(id);

    // --- Ver documento ---
    const handleVerDocumento = async (carta) => {
        const detalle = await obtenerCartaPoderDetalle(carta.Id);
        if (detalle) {
            setDocumentoViewer({
                open: true,
                titulo: `Carta Poder - ${carta.NumeroCartaPoder}`,
                contenido: detalle.ContenidoHTML,
                carta: carta
            });
        }
    };

    const handleCerrarViewer = () => {
        setDocumentoViewer({ open: false, titulo: '', contenido: '', carta: null });
    };

    // --- Generar PDF individual con jsPDF (texto vectorial) ---
    const generarPdfDeCarta = async (carta) => {
        const detalle = await obtenerCartaPoderDetalle(carta.Id);
        if (!detalle || !detalle.ContenidoHTML) return null;

        // Parsear HTML en DOM temporal
        const domParser = new DOMParser();
        const htmlDoc = domParser.parseFromString(detalle.ContenidoHTML, 'text/html');

        // Extraer margenes del atributo data-sigif-margins
        let margins = { top: 25.4, right: 25.4, bottom: 25.4, left: 25.4 };
        const marginEl = htmlDoc.querySelector('[data-sigif-margins]');
        if (marginEl) {
            const marginStr = marginEl.getAttribute('data-sigif-margins');
            const parsed = {};
            marginStr.split(';').forEach(part => {
                const [key, val] = part.split(':');
                if (key && val) parsed[key.trim()] = parseFloat(val);
            });
            if (parsed.top) margins.top = parsed.top;
            if (parsed.right) margins.right = parsed.right;
            if (parsed.bottom) margins.bottom = parsed.bottom;
            if (parsed.left) margins.left = parsed.left;
        }

        const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const contentW = pageW - margins.left - margins.right;
        let cursorY = margins.top;

        // Fuente base: Times New Roman = times en jsPDF
        const FONT = 'times';
        const FONT_SIZE = 12;
        const PT_TO_MM = 0.3528; // 1pt = 0.3528mm

        // Funcion para salto de pagina si no hay espacio
        const checkPageBreak = (needed) => {
            if (cursorY + needed > pageH - margins.bottom) {
                doc.addPage();
                cursorY = margins.top;
            }
        };

        // Funcion para extraer texto con formato de un nodo (recorre el DOM recursivamente)
        const extractTextRuns = (node) => {
            const runs = [];
            const walk = (el, inherited) => {
                if (el.nodeType === Node.TEXT_NODE) {
                    const text = el.textContent;
                    if (text.trim() || text.includes(' ')) {
                        runs.push({ text, ...inherited });
                    }
                    return;
                }
                if (el.nodeType !== Node.ELEMENT_NODE) return;

                // Ignorar elementos de imagen (se procesan aparte)
                if (el.tagName.toLowerCase() === 'img') return;

                const tag = el.tagName.toLowerCase();
                const style = {};

                // Detectar bold
                if (tag === 'b' || tag === 'strong' ||
                    (el.style && el.style.fontWeight && parseInt(el.style.fontWeight) >= 700)) {
                    style.bold = true;
                }
                // Detectar underline
                if (tag === 'u' || (el.style && el.style.textDecoration === 'underline')) {
                    style.underline = true;
                }

                const merged = { ...inherited, ...style };

                for (const child of el.childNodes) {
                    walk(child, merged);
                }
            };
            walk(node, {});
            return runs;
        };

        // Funcion para renderizar texto con formato mixto (bold/normal/underline)
        // Maneja word-wrap y justificado real
        const renderFormattedText = (runs, align, lineHeightMm) => {
            if (runs.length === 0) return;

            doc.setFontSize(FONT_SIZE);
            const lh = lineHeightMm;

            // Consolidar runs: unir texto y separar en palabras individuales con su formato
            // Cada "word" es { text: "palabra", bold, underline }
            const words = [];
            for (const run of runs) {
                // Normalizar espacios multiples a uno solo
                const normalized = run.text.replace(/\s+/g, ' ');
                // Separar en palabras (sin espacios vacios)
                const parts = normalized.split(' ');
                for (let i = 0; i < parts.length; i++) {
                    if (parts[i].length > 0) {
                        words.push({ text: parts[i], bold: !!run.bold, underline: !!run.underline });
                    }
                }
            }

            if (words.length === 0) return;

            // Ancho de un espacio normal
            doc.setFont(FONT, 'normal');
            const spaceW = doc.getTextWidth(' ');

            // Construir lineas respetando el ancho disponible
            const lines = [];
            let currentLine = [];
            let currentWidth = 0;

            for (const word of words) {
                doc.setFont(FONT, word.bold ? 'bold' : 'normal');
                const wordW = doc.getTextWidth(word.text);
                const neededW = currentLine.length > 0 ? spaceW + wordW : wordW;

                if (currentWidth + neededW > contentW && currentLine.length > 0) {
                    lines.push([...currentLine]);
                    currentLine = [word];
                    currentWidth = wordW;
                } else {
                    currentLine.push(word);
                    currentWidth += neededW;
                }
            }
            if (currentLine.length > 0) {
                lines.push(currentLine);
            }

            // Renderizar cada linea
            for (let li = 0; li < lines.length; li++) {
                const line = lines[li];
                checkPageBreak(lh);

                // Calcular ancho total de palabras (sin espacios)
                let wordsWidth = 0;
                for (const w of line) {
                    doc.setFont(FONT, w.bold ? 'bold' : 'normal');
                    wordsWidth += doc.getTextWidth(w.text);
                }
                const totalWithNormalSpaces = wordsWidth + spaceW * (line.length - 1);

                const isLastLine = li === lines.length - 1;
                const shouldJustify = align === 'justify' && !isLastLine && line.length > 1;

                // Calcular espacio entre palabras
                let gapBetweenWords = spaceW;
                if (shouldJustify) {
                    gapBetweenWords = (contentW - wordsWidth) / (line.length - 1);
                }

                // Calcular posicion X inicial
                let x = margins.left;
                if (align === 'center' && !shouldJustify) {
                    x = margins.left + (contentW - totalWithNormalSpaces) / 2;
                } else if (align === 'right' && !shouldJustify) {
                    x = margins.left + contentW - totalWithNormalSpaces;
                }

                // Dibujar cada palabra
                for (let wi = 0; wi < line.length; wi++) {
                    const w = line[wi];
                    doc.setFont(FONT, w.bold ? 'bold' : 'normal');
                    doc.text(w.text, x, cursorY);

                    const tw = doc.getTextWidth(w.text);

                    if (w.underline) {
                        doc.setLineWidth(0.3);
                        doc.line(x, cursorY + 0.5, x + tw, cursorY + 0.5);
                    }

                    if (wi < line.length - 1) {
                        x += tw + gapBetweenWords;
                    }
                }

                cursorY += lh;
            }
        };

        // Obtener alineacion de un elemento
        const getAlign = (el) => {
            const style = el.getAttribute('style') || '';
            if (style.includes('text-align: center') || style.includes('text-align:center')) return 'center';
            if (style.includes('text-align: right') || style.includes('text-align:right')) return 'right';
            if (style.includes('text-align: justify') || style.includes('text-align:justify')) return 'justify';
            return 'left';
        };

        // Obtener line-height de un elemento (devuelve mm)
        const getLineHeight = (el) => {
            const style = el.getAttribute('style') || '';
            // Buscar line-height como multiplicador (ej: 1.1, 1.15, 115%)
            const match = style.match(/line-height:\s*([\d.]+)(%|;|\s|$)/);
            if (match) {
                let val = parseFloat(match[1]);
                if (match[2] === '%') val = val / 100; // 115% -> 1.15
                if (val < 10) return FONT_SIZE * PT_TO_MM * val;
            }
            // Default: 115% como Word
            return FONT_SIZE * PT_TO_MM * 1.15;
        };

        // Obtener margin-bottom de un elemento (devuelve mm)
        const getMarginBottom = (el) => {
            const style = el.getAttribute('style') || '';
            // margin-bottom: 0cm o 0 = sin espacio extra
            if (style.includes('margin-bottom: 0') || style.includes('margin-bottom:0')) return 0;
            // Default Word MsoNormal: 8pt after (~2.8mm)
            return 8 * PT_TO_MM;
        };

        // Si el HTML no contiene ninguna imagen (escudo), agregar escudo al inicio
        const tieneImagen = htmlDoc.body.querySelector('img') !== null;
        if (!tieneImagen && ESCUDO_BASE64) {
            try {
                checkPageBreak(ESCUDO_HEIGHT_MM + 2);
                const imgX = margins.left + (contentW - ESCUDO_WIDTH_MM) / 2;
                doc.addImage(ESCUDO_BASE64, 'PNG', imgX, cursorY, ESCUDO_WIDTH_MM, ESCUDO_HEIGHT_MM);
                cursorY += ESCUDO_HEIGHT_MM + 3;
            } catch (e) {
                console.warn('Error al agregar escudo al PDF:', e);
            }
        }

        // Procesar cada elemento de nivel superior
        const body = htmlDoc.body;
        const elements = body.querySelectorAll(':scope > p, :scope > div');

        for (const el of elements) {
            // Ignorar el elemento oculto de margenes
            if (el.id === 'sigif-margins' || (el.style && el.style.display === 'none')) continue;

            const align = getAlign(el);
            const lineH = getLineHeight(el);
            const marginBottom = getMarginBottom(el);

            // Buscar imagen en este elemento
            const img = el.querySelector('img');
            if (img && img.src) {
                // Cargar imagen y agregar al PDF
                try {
                    const imgData = img.src;
                    const imgW = parseInt(img.getAttribute('width') || '76');
                    const imgH = parseInt(img.getAttribute('height') || '120');
                    // Convertir px a mm (96dpi: 1px = 25.4/96 = 0.2646mm)
                    const pxToMm = 25.4 / 96;
                    const wMm = imgW * pxToMm;
                    const hMm = imgH * pxToMm;

                    checkPageBreak(hMm + 2);

                    const imgX = align === 'center'
                        ? margins.left + (contentW - wMm) / 2
                        : margins.left;

                    doc.addImage(imgData, 'PNG', imgX, cursorY, wMm, hMm);
                    cursorY += hMm + 3; // espacio despues de la imagen
                } catch (e) {
                    console.warn('Error al agregar imagen al PDF:', e);
                }
            } else {
                // Parrafo de solo texto
                const text = el.textContent.replace(/\s+/g, ' ').trim();
                if (!text || text === '\u00a0') {
                    // Parrafo vacio: espacio minimo
                    cursorY += lineH * 0.3;
                    continue;
                }

                const runs = extractTextRuns(el);
                renderFormattedText(runs, align, lineH);
            }

            // Espacio entre parrafos
            cursorY += marginBottom;
        }

        // Generar blob
        const pdfBlob = doc.output('blob');
        return pdfBlob;
    };

    const handleGenerarPdf = async (carta) => {
        setGenerandoPdf(carta.Id);
        try {
            const pdfBlob = await generarPdfDeCarta(carta);
            if (!pdfBlob) {
                enqueueSnackbar('No se pudo obtener el contenido de la carta', { variant: 'error' });
                return;
            }
            const response = await subirPdfCartaPoder(carta.Id, pdfBlob);
            if (response) {
                await cargarCartas();
            }
        } catch (error) {
            console.error('Error al generar PDF:', error);
            enqueueSnackbar('Error al generar PDF', { variant: 'error' });
        } finally {
            setGenerandoPdf(null);
        }
    };

    // --- Descargar PDF individual ---
    const handleDescargarPdf = async (carta, firmado = false) => {
        try {
            const blob = await descargarPdfBlob(carta.Id, firmado);
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `CartaPoder_${(carta.NumeroCartaPoder || '').replace(/\//g, '-')}${firmado ? '_firmado' : ''}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error al descargar PDF:', error);
            enqueueSnackbar(error.message || 'Error al descargar PDF', { variant: 'error' });
        }
    };

    // --- Descarga masiva como ZIP ---
    const handleDescargarMasivo = async () => {
        const cartasConPdf = cartasFiltradas.filter(c => selected.includes(c.Id) && c.TienePDF);
        if (cartasConPdf.length === 0) return;

        setProgresoDialog({
            open: true, total: cartasConPdf.length, actual: 0,
            generados: 0, errores: 0, currentName: '', tipo: 'descargar'
        });

        const zip = new JSZip();
        let descargados = 0;
        let errores = 0;

        for (let i = 0; i < cartasConPdf.length; i++) {
            const carta = cartasConPdf[i];
            const firmado = carta.EstadoFirma === 2;
            const nombreArchivo = `CartaPoder_${(carta.NumeroCartaPoder || '').replace(/\//g, '-')}${firmado ? '_firmado' : ''}.pdf`;

            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({
                ...prev, actual: i + 1,
                currentName: carta.NumeroCartaPoder || `Carta ${i + 1}`
            }));

            try {
                const blob = await descargarPdfBlob(carta.Id, firmado);
                zip.file(nombreArchivo, blob);
                descargados++;
            } catch {
                errores++;
            }

            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({ ...prev, generados: descargados, errores }));
        }

        // Generar y descargar el ZIP
        if (descargados > 0) {
            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({ ...prev, currentName: 'Generando archivo ZIP...' }));
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, `CartasPoder_${new Date().toISOString().slice(0, 10)}.zip`);
        }

        setProgresoDialog(prev => ({ ...prev, open: false }));
        enqueueSnackbar(
            `${descargados} PDF(s) descargado(s) en ZIP${errores > 0 ? `, ${errores} error(es)` : ''}`,
            { variant: errores > 0 ? 'warning' : 'success' }
        );
    };

    // --- Subir firmado individual ---
    const handleAbrirUpload = (carta) => {
        setUploadDialog({ open: true, carta: carta });
    };

    const handleSubirFirmado = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
            enqueueSnackbar('El archivo debe ser un PDF', { variant: 'error' });
            return;
        }

        const carta = uploadDialog.carta;
        const response = await subirPdfFirmado(carta.Id, file);
        if (response) {
            setUploadDialog({ open: false, carta: null });
            await cargarCartas();
        }

        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // --- Subida masiva de PDFs firmados ---
    const handleSubirMasivoFirmado = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        // Validar que todos sean PDFs
        const noPdf = files.filter(f => !f.name.toLowerCase().endsWith('.pdf'));
        if (noPdf.length > 0) {
            enqueueSnackbar(`${noPdf.length} archivo(s) no son PDF y fueron ignorados`, { variant: 'warning' });
        }
        const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
        if (pdfFiles.length === 0) {
            enqueueSnackbar('No se encontraron archivos PDF', { variant: 'error' });
            if (bulkUploadRef.current) bulkUploadRef.current.value = '';
            return;
        }

        // Validar formato de nombre: debe contener "_firmado" (case insensitive)
        const sinFirmado = pdfFiles.filter(f => !/_firmado/i.test(f.name.replace(/\.pdf$/i, '')));
        if (sinFirmado.length > 0) {
            enqueueSnackbar(
                `${sinFirmado.length} archivo(s) no tienen "_firmado" en el nombre (ej: CartaPoder_A-000251-26_firmado.pdf). Verifique los archivos.`,
                { variant: 'error', autoHideDuration: 8000 }
            );
            if (bulkUploadRef.current) bulkUploadRef.current.value = '';
            return;
        }

        setProgresoDialog({
            open: true, total: pdfFiles.length, actual: 0,
            generados: 0, errores: 0, currentName: '', tipo: 'subir'
        });

        let subidos = 0;
        let errores = 0;
        let noCoincidieron = 0;

        for (let i = 0; i < pdfFiles.length; i++) {
            const file = pdfFiles[i];
            // Extraer numero de carta del nombre: CartaPoder_X-XXXXXX-XX_firmado.pdf
            const filename = file.name.replace(/\.pdf$/i, '').replace(/_firmado/i, '');

            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({
                ...prev, actual: i + 1, currentName: file.name
            }));

            // Buscar carta que coincida por numero en el nombre del archivo
            const carta = cartas.find(c => {
                const nroNormalizado = (c.NumeroCartaPoder || '').replace(/\//g, '-');
                return nroNormalizado && filename.includes(nroNormalizado) && c.EstadoFirma === 1;
            });

            if (carta) {
                try {
                    const response = await subirPdfFirmado(carta.Id, file);
                    if (response) {
                        subidos++;
                    } else {
                        errores++;
                    }
                } catch {
                    errores++;
                }
            } else {
                noCoincidieron++;
            }

            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({
                ...prev, generados: subidos, errores: errores + noCoincidieron
            }));
        }

        await cargarCartas();
        setProgresoDialog(prev => ({ ...prev, open: false }));

        let mensaje = `${subidos} PDF(s) firmado(s) subido(s)`;
        if (noCoincidieron > 0) mensaje += `, ${noCoincidieron} no coincidieron con cartas pendientes`;
        if (errores > 0) mensaje += `, ${errores} error(es)`;

        enqueueSnackbar(mensaje, { variant: (errores > 0 || noCoincidieron > 0) ? 'warning' : 'success' });

        if (bulkUploadRef.current) bulkUploadRef.current.value = '';
    };

    // --- Firma digital desde el sistema ---
    const handleAbrirFirmaDigital = () => {
        if (!servicioFirma.disponible) {
            enqueueSnackbar(
                'El servicio de firma local no está disponible. Verifique que SIGIF Firma Local esté ejecutándose.',
                { variant: 'error', autoHideDuration: 6000 }
            );
            return;
        }
        if (servicioFirma.tokens === 0) {
            enqueueSnackbar(
                'No se detectó ningún token USB. Verifique que el token esté conectado.',
                { variant: 'warning', autoHideDuration: 6000 }
            );
            return;
        }
        setFirmaDialog({ open: true, pin: '', firmando: false });
    };

    const handleFirmarDigitalmente = async () => {
        const pin = firmaDialog.pin;
        if (!pin) {
            enqueueSnackbar('Ingrese el PIN del token', { variant: 'warning' });
            return;
        }

        // Obtener cartas pendientes (sin PDF o con PDF sin firmar)
        let cartasParaFirmar = cartasFiltradas.filter(c => c.EstadoFirma === 0 || c.EstadoFirma === 1);
        if (selected.length > 0) {
            cartasParaFirmar = cartasParaFirmar.filter(c => selected.includes(c.Id));
        }

        if (cartasParaFirmar.length === 0) {
            enqueueSnackbar('No hay cartas pendientes de firma', { variant: 'warning' });
            return;
        }

        setFirmaDialog(prev => ({ ...prev, firmando: true }));
        setProgresoDialog({
            open: true, total: cartasParaFirmar.length, actual: 0,
            generados: 0, errores: 0, currentName: '', tipo: 'firmar'
        });

        let firmados = 0;
        let errores = 0;

        for (let i = 0; i < cartasParaFirmar.length; i++) {
            const carta = cartasParaFirmar[i];
            setProgresoDialog(prev => ({
                ...prev, actual: i + 1,
                currentName: carta.NumeroCartaPoder || `Carta ${i + 1}`
            }));

            try {
                // 0. Si no tiene PDF, generarlo primero
                if (carta.EstadoFirma === 0) {
                    setProgresoDialog(prev => ({ ...prev, currentName: `Generando PDF: ${carta.NumeroCartaPoder || `Carta ${i + 1}`}` }));
                    const pdfGenBlob = await generarPdfDeCarta(carta);
                    if (pdfGenBlob) {
                        await subirPdfCartaPoder(carta.Id, pdfGenBlob);
                    } else {
                        errores++;
                        continue;
                    }
                    setProgresoDialog(prev => ({ ...prev, currentName: `Firmando: ${carta.NumeroCartaPoder || `Carta ${i + 1}`}` }));
                }

                // 1. Descargar el PDF sin firmar desde el servidor
                const pdfBlob = await descargarPdfBlob(carta.Id, false);

                // 2. Enviar al servicio local para firmar
                const firmaResp = await fetchFirmaLocal(`/firmar?pin=${encodeURIComponent(pin)}`, {
                    method: 'POST',
                    body: pdfBlob,
                    headers: { 'Content-Type': 'application/pdf' }
                });

                if (!firmaResp.ok) {
                    const errorData = await firmaResp.json().catch(() => null);
                    if (errorData?.tipo === 'token') {
                        // Error de PIN u otro error de token: detener todo
                        enqueueSnackbar(errorData.error || 'Error con el token USB', { variant: 'error' });
                        break;
                    }
                    throw new Error(errorData?.error || 'Error al firmar');
                }

                const signedPdfBlob = await firmaResp.blob();

                // 3. Subir el PDF firmado al servidor SIGIF
                const uploadResp = await subirPdfFirmado(carta.Id, signedPdfBlob);
                if (uploadResp) {
                    firmados++;
                } else {
                    errores++;
                }
            } catch (err) {
                console.error(`Error al firmar carta ${carta.NumeroCartaPoder}:`, err);
                errores++;
                // PIN mal o el cliente de firma se cerró: detener
                if (err.cortar) { enqueueSnackbar(err.message, { variant: 'error' }); break; }
                if (err.message?.includes('PIN')) break;
            }

            // eslint-disable-next-line no-loop-func
            setProgresoDialog(prev => ({ ...prev, generados: firmados, errores }));
        }

        await cargarCartas();
        setProgresoDialog(prev => ({ ...prev, open: false }));
        setFirmaDialog({ open: false, pin: '', firmando: false });

        let mensaje = `${firmados} carta(s) firmada(s) digitalmente`;
        if (errores > 0) mensaje += `, ${errores} error(es)`;
        enqueueSnackbar(mensaje, { variant: errores > 0 ? 'warning' : 'success' });
    };

    // --- Formatear fecha seguro ---
    const formatearFecha = (fecha) => {
        if (!fecha) return '-';
        try {
            const d = new Date(fecha);
            if (isNaN(d)) return fecha;
            return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
        } catch { return fecha; }
    };

    // --- Estado chip ---
    const getEstadoChip = (carta) => {
        if (carta.EstadoFirma === 2) {
            return <Chip label="Firmado" size="small" sx={{ ...styles.estadoChip, ...styles.firmado }} icon={<CheckCircleIcon style={{ color: 'white' }} />} />;
        } else {
            return <Chip label="Pendiente" size="small" sx={{ ...styles.estadoChip, ...styles.pendiente }} icon={<WarningIcon style={{ color: 'white' }} />} />;
        }
    };

    // Conteos de seleccion
    const selectedConPdf = cartasFiltradas.filter(c => selected.includes(c.Id) && c.TienePDF).length;
    const selectedParaFirmar = cartasFiltradas.filter(c => selected.includes(c.Id) && (c.EstadoFirma === 0 || c.EstadoFirma === 1)).length;

    // Label del progreso segun tipo
    const progresoLabel = progresoDialog.tipo === 'generar' ? 'Generando PDFs'
        : progresoDialog.tipo === 'descargar' ? 'Descargando PDFs'
        : progresoDialog.tipo === 'firmar' ? 'Firmando digitalmente'
        : 'Subiendo PDFs firmados';

    return (
        <>
            {/* Filters + Table */}
            <Paper sx={styles.paper}>
                <Box sx={styles.filterRow}>
                    <Chip label={`${resumen.sinPdf + resumen.pendientes} Pendientes`} color={filtroEstado === 'pendiente' ? 'warning' : 'default'}
                        onClick={() => handleChipFiltro('pendiente')}
                        sx={{...styles.chipFiltro, ...(filtroEstado === 'pendiente' ? styles.chipActivo : {})}} />
                    <Chip label={`${resumen.firmadas} Firmadas`} color={filtroEstado === 2 ? 'success' : 'default'}
                        onClick={() => handleChipFiltro(2)}
                        sx={{...styles.chipFiltro, ...(filtroEstado === 2 ? styles.chipActivo : {})}} />
                    <Chip label={`${resumen.total} Total`} color={filtroEstado === null ? 'primary' : 'default'}
                        onClick={() => handleChipFiltro(null)}
                        sx={{...styles.chipFiltro, ...(filtroEstado === null ? styles.chipActivo : {})}} />

                    <Box sx={{ flexGrow: 1 }} />

                    <TextField
                        size="small" placeholder="Buscar..."
                        value={filtroBusqueda} onChange={(e) => setFiltroBusqueda(e.target.value)}
                        style={{ width: 300 }}
                        InputProps={{ startAdornment: <SearchIcon style={{ color: '#999', marginRight: 8 }} /> }}
                    />
                    <Tooltip title="Recargar">
                        <span><IconButton onClick={cargarCartas} disabled={loading}><RefreshIcon /></IconButton></span>
                    </Tooltip>
                </Box>

                {/* Bulk actions */}
                {selected.length > 0 && (
                    <Box sx={styles.bulkBar}>
                        <Typography variant="body2" style={{ fontWeight: 600 }}>{selected.length} seleccionada(s)</Typography>
                        {selectedConPdf > 0 && (
                            <Button variant="outlined" size="small" startIcon={<DownloadIcon />}
                                onClick={handleDescargarMasivo} disabled={progresoDialog.open || loading}>
                                Descargar {selectedConPdf} PDF(s)
                            </Button>
                        )}
                        {selectedParaFirmar > 0 && servicioFirma.disponible && servicioFirma.tokens > 0 && (
                            <Button variant="contained" size="small" style={{ backgroundColor: '#2e7d32', color: 'white' }}
                                startIcon={<TokenIcon />} onClick={handleAbrirFirmaDigital}
                                disabled={progresoDialog.open || loading || firmaDialog.firmando}>
                                Firmar {selectedParaFirmar} con Token
                            </Button>
                        )}
                        {selectedParaFirmar > 0 && (servicioFirma.disponible === false || servicioFirma.tokens === 0) && (
                            <Tooltip title="Servicio de firma local no disponible">
                                <span>
                                    <Button variant="outlined" size="small" style={{ borderColor: '#ccc', color: '#999' }}
                                        startIcon={<TokenIcon />} disabled>
                                        Firma no disponible
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button size="small" onClick={() => setSelected([])}>Deseleccionar</Button>
                    </Box>
                )}

                {/* Action bar */}
                {selected.length === 0 && (
                    <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
                        {servicioFirma.disponible && servicioFirma.tokens > 0 && (
                            <Button variant="contained" size="small" style={{ backgroundColor: '#2e7d32', color: 'white' }}
                                startIcon={<TokenIcon />} onClick={handleAbrirFirmaDigital}
                                disabled={progresoDialog.open || loading || firmaDialog.firmando}>
                                Firmar con Token USB
                            </Button>
                        )}
                        {(servicioFirma.disponible === false || servicioFirma.tokens === 0) && (
                            <Tooltip title={servicioFirma.disponible ? "Token no detectado. Inserte el token USB." : "El servicio de firma local no está disponible. Verifique que SIGIF Firma Local esté ejecutándose."}>
                                <span>
                                    <Button variant="outlined" size="small" style={{ borderColor: '#ccc', color: '#999' }}
                                        startIcon={<TokenIcon />} disabled>
                                        Firma Digital no disponible
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {loading ? (
                    <Box sx={styles.loadingBox}><CircularProgress /></Box>
                ) : (
                    <>
                    <TableContainer sx={styles.tableContainer}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selected.length > 0 && selected.length < cartasFiltradas.length}
                                            checked={cartasFiltradas.length > 0 && selected.length === cartasFiltradas.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }} sortDirection={orderBy === 'IdLote' ? orderDir : false}>
                                        <TableSortLabel active={orderBy === 'IdLote'} direction={orderBy === 'IdLote' ? orderDir : 'asc'} onClick={() => handleSort('IdLote')}>
                                            Lote
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }} sortDirection={orderBy === 'NumeroCartaPoder' ? orderDir : false}>
                                        <TableSortLabel active={orderBy === 'NumeroCartaPoder'} direction={orderBy === 'NumeroCartaPoder' ? orderDir : 'asc'} onClick={() => handleSort('NumeroCartaPoder')}>
                                            Nro. Carta Poder
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }} sortDirection={orderBy === 'Autos' ? orderDir : false}>
                                        <TableSortLabel active={orderBy === 'Autos'} direction={orderBy === 'Autos' ? orderDir : 'asc'} onClick={() => handleSort('Autos')}>
                                            Expediente
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }} sortDirection={orderBy === 'Caratula' ? orderDir : false}>
                                        <TableSortLabel active={orderBy === 'Caratula'} direction={orderBy === 'Caratula' ? orderDir : 'asc'} onClick={() => handleSort('Caratula')}>
                                            Caratula
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }} sortDirection={orderBy === 'EstadoFirma' ? orderDir : false}>
                                        <TableSortLabel active={orderBy === 'EstadoFirma'} direction={orderBy === 'EstadoFirma' ? orderDir : 'asc'} onClick={() => handleSort('EstadoFirma')}>
                                            Estado
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600 }} sortDirection={orderBy === 'FechaFirma' ? orderDir : false}>
                                        <TableSortLabel active={orderBy === 'FechaFirma'} direction={orderBy === 'FechaFirma' ? orderDir : 'asc'} onClick={() => handleSort('FechaFirma')}>
                                            Fecha Firma
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ fontWeight: 600, textAlign: 'center' }}>Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cartasFiltradas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                No hay cartas poder para mostrar
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    cartasFiltradas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((carta, idx) => (
                                        <TableRow
                                            key={carta.Id}
                                            hover
                                            selected={isSelected(carta.Id)}
                                            style={carta.EstadoFirma === 2 ? { backgroundColor: '#e8f5e9' } : undefined}
                                            sx={{ backgroundColor: carta.EstadoFirma !== 2 ? (idx % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent") : undefined }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isSelected(carta.Id)}
                                                    onChange={() => handleSelectOne(carta.Id)}
                                                />
                                            </TableCell>
                                            <TableCell>{carta.IdLote}</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>{carta.NumeroCartaPoder}</TableCell>
                                            <TableCell>{carta.Autos || '-'}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" noWrap style={{ maxWidth: 200 }}>
                                                    {carta.Caratula || '-'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{getEstadoChip(carta)}</TableCell>
                                            <TableCell>{formatearFecha(carta.FechaFirma)}</TableCell>
                                            <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Ver documento">
                                                    <IconButton size="small" onClick={() => handleVerDocumento(carta)} sx={styles.actionButton}>
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {carta.EstadoFirma === 0 && (
                                                    <Tooltip title="Generar PDF">
                                                        <IconButton size="small" color="primary" onClick={() => handleGenerarPdf(carta)} disabled={generandoPdf === carta.Id} sx={styles.actionButton}>
                                                            {generandoPdf === carta.Id ? <CircularProgress size={18} /> : <DescriptionIcon />}
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {carta.TienePDFFirmado && (
                                                    <Tooltip title="Descargar PDF firmado">
                                                        <IconButton size="small" style={{ color: '#4caf50' }} onClick={() => handleDescargarPdf(carta, true)} sx={styles.actionButton}>
                                                            <CheckCircleIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {(carta.TienePDF || carta.EstadoFirma === 1) && (
                                                    <Tooltip title="Más opciones">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuCarta(carta); }}
                                                            sx={styles.actionButton}
                                                        >
                                                            <MoreVertIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={cartasFiltradas.length}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[25, 50, 100]}
                        labelRowsPerPage="Por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                    </>
                )}
            </Paper>
            {/* Visor de documento */}
            <DocumentoViewer
                open={documentoViewer.open}
                onClose={handleCerrarViewer}
                titulo={documentoViewer.titulo}
                contenido={documentoViewer.contenido}
                nombreArchivo={documentoViewer.carta ? `CartaPoder_${(documentoViewer.carta.NumeroCartaPoder || '').replace(/\//g, '-')}` : 'documento'}
                onDescargarPdf={documentoViewer.carta ? async () => {
                    const pdfBlob = await generarPdfDeCarta(documentoViewer.carta);
                    if (pdfBlob) {
                        const blobUrl = URL.createObjectURL(pdfBlob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = `CartaPoder_${(documentoViewer.carta.NumeroCartaPoder || '').replace(/\//g, '-')}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                    }
                } : undefined}
            />
            {/* Dialog para subir PDF firmado individual */}
            <Dialog
                open={uploadDialog.open}
                onClose={() => setUploadDialog({ open: false, carta: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <UploadIcon color="primary" />
                        Subir PDF Firmado
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Suba el PDF firmado de la Carta Poder <strong>{uploadDialog.carta?.NumeroCartaPoder}</strong>.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        El archivo debe ser un PDF firmado digitalmente con su token.
                    </Typography>
                    <Box
                        component="input"
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleSubirFirmado}
                        ref={fileInputRef}
                        sx={styles.uploadInput}
                        id="upload-pdf-firmado-readj"
                    />
                    <label htmlFor="upload-pdf-firmado-readj">
                        <Button variant="contained" color="primary" component="span" startIcon={<UploadIcon />} disabled={loading} fullWidth>
                            {loading ? 'Subiendo...' : 'Seleccionar archivo PDF'}
                        </Button>
                    </label>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadDialog({ open: false, carta: null })}>
                        Cancelar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Input oculto para subida masiva */}
            <input
                type="file"
                accept=".pdf,application/pdf"
                multiple
                ref={bulkUploadRef}
                style={{ display: 'none' }}
                onChange={handleSubirMasivoFirmado}
            />
            {/* Dialog de progreso */}
            <Dialog open={progresoDialog.open} maxWidth="sm" fullWidth>
                <DialogTitle>{progresoLabel}</DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <Typography variant="body2" gutterBottom>
                            Procesando {progresoDialog.actual} de {progresoDialog.total}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {progresoDialog.currentName}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progresoDialog.total > 0 ? (progresoDialog.actual / progresoDialog.total) * 100 : 0}
                        style={{ height: 8, borderRadius: 4 }}
                    />
                    <Box mt={1} display="flex" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                            {progresoDialog.generados} procesado(s)
                        </Typography>
                        {progresoDialog.errores > 0 && (
                            <Typography variant="caption" color="error">
                                {progresoDialog.errores} error(es)
                            </Typography>
                        )}
                    </Box>
                </DialogContent>
            </Dialog>
            {/* Dialog para ingresar PIN del token USB */}
            <Dialog
                open={firmaDialog.open}
                onClose={() => !firmaDialog.firmando && setFirmaDialog({ open: false, pin: '', firmando: false })}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <TokenIcon style={{ color: '#2e7d32' }} />
                        Firma Digital con Token USB
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography paragraph>
                        Se firmarán <strong>
                            {selected.length > 0
                                ? cartasFiltradas.filter(c => selected.includes(c.Id) && c.EstadoFirma === 1).length
                                : cartasFiltradas.filter(c => c.EstadoFirma === 1).length
                            }
                        </strong> carta(s) poder pendientes de firma.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Ingrese el PIN de su token USB para firmar digitalmente.
                        {servicioFirma.tokens === 0 && (
                            <span style={{ color: '#e65100', display: 'block', marginTop: 4 }}>
                                No se detectó token USB conectado. Conéctelo antes de firmar.
                            </span>
                        )}
                    </Typography>
                    <TextField
                        label="PIN del Token"
                        type="password"
                        variant="outlined"
                        fullWidth
                        autoFocus
                        value={firmaDialog.pin}
                        onChange={(e) => setFirmaDialog(prev => ({ ...prev, pin: e.target.value }))}
                        disabled={firmaDialog.firmando}
                        onKeyDown={(e) => e.key === 'Enter' && !firmaDialog.firmando && handleFirmarDigitalmente()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setFirmaDialog({ open: false, pin: '', firmando: false })}
                        disabled={firmaDialog.firmando}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        style={{ backgroundColor: '#2e7d32', color: 'white' }}
                        onClick={handleFirmarDigitalmente}
                        disabled={firmaDialog.firmando || !firmaDialog.pin}
                        startIcon={firmaDialog.firmando ? <CircularProgress size={18} color="inherit" /> : <TokenIcon />}
                    >
                        {firmaDialog.firmando ? 'Firmando...' : 'Firmar'}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Menu contextual de opciones secundarias por fila */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => { setMenuAnchor(null); setMenuCarta(null); }}
            >
                {menuCarta?.TienePDF && menuCarta?.EstadoFirma !== 2 && (
                    <MenuItem onClick={() => {
                        handleDescargarPdf(menuCarta, false);
                        setMenuAnchor(null); setMenuCarta(null);
                    }}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Descargar PDF sin firmar</ListItemText>
                    </MenuItem>
                )}
                {menuCarta?.EstadoFirma === 1 && (
                    <MenuItem onClick={() => {
                        handleAbrirUpload(menuCarta);
                        setMenuAnchor(null); setMenuCarta(null);
                    }}>
                        <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Subir PDF firmado manualmente</ListItemText>
                    </MenuItem>
                )}
                {menuCarta?.TienePDF && menuCarta?.EstadoFirma === 2 && (
                    <MenuItem onClick={() => {
                        handleDescargarPdf(menuCarta, false);
                        setMenuAnchor(null); setMenuCarta(null);
                    }}>
                        <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>Descargar PDF original (sin firma)</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};

export default FirmaCartasPoder;
